import React, { useState, useRef, useEffect } from 'react';
import {
    X, Send, Heart, MessageCircle, Repeat2, ThumbsUp, MoreHorizontal, Image, Loader2, AlertCircle, CheckCircle, Save, RotateCcw, FileText
} from 'lucide-react';
import vaultService from '../../../services/vaultService';

/**
 * LinkedInPreview - Dynamic LinkedIn post preview component
 * 
 * Displays a LinkedIn-style post preview with support for:
 * - Dynamic content injection
 * - Streaming content display (typing effect)
 * - Hashtag highlighting
 * - Guest name display
 * - Inline content editing
 * - Media attachment upload via Vault
 * - Drag-and-drop image upload
 * - Unsaved changes tracking
 * - Save to Vault functionality
 * - Start Over functionality
 * 
 * @param {object} props
 * @param {string} props.content - The post content to display
 * @param {boolean} props.isStreaming - Whether content is being streamed
 * @param {string} props.guestName - Guest name for episode display
 * @param {boolean} props.isEditable - Whether content can be edited inline
 * @param {function} props.onContentChange - Callback when content changes
 * @param {boolean} props.hasUnsavedChanges - Whether there are unsaved changes
 * @param {function} props.onSave - Callback to save changes
 * @param {object} props.media - Current media attachment { url, name, type, size, id }
 * @param {function} props.onMediaChange - Callback when media changes (file or null)
 * @param {boolean} props.isUploading - Whether media is being uploaded
 * @param {object} props.episode - Episode data for upload context
 * @param {function} props.onSaveToVault - Callback to save post to vault
 * @param {function} props.onStartOver - Callback to reset and start over
 * @param {boolean} props.isSavingToVault - Whether save to vault is in progress
 */
const LinkedInPreview = ({
    content = null,
    isStreaming = false,
    guestName = null,
    isEditable = false,
    onContentChange = null,
    hasUnsavedChanges = false,
    onSave = null,
    media = null,
    onMediaChange = null,
    isUploading = false,
    episode = null,
    onSaveToVault = null,
    onStartOver = null,
    isSavingToVault = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [isDraggingImage, setIsDraggingImage] = useState(false);
    const [isDraggingText, setIsDraggingText] = useState(false);
    const [isInternalUploading, setIsInternalUploading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    // Drag counters to prevent flashing from child element events
    const imageDragCounterRef = useRef(0);
    const textDragCounterRef = useRef(0);

    // Default placeholder content
    const defaultContent = `Just had an incredible conversation with an amazing guest about the future of AI in podcasting! 

We covered:
- Building successful startups from scratch
- The role of AI in content creation
- Future trends in podcasting

Listen now on your favorite platform! 

#Podcasting #AI #StartupGrowth`;

    const displayContent = content || defaultContent;

    // Format content with hashtag highlighting
    const formatContent = (text) => {
        if (!text) return null;

        // Split by hashtags while keeping them
        const parts = text.split(/(#[\w]+)/g);

        return parts.map((part, index) => {
            if (part.startsWith('#')) {
                return (
                    <span key={index} className="text-blue-600 font-semibold hover:underline cursor-pointer">
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    // Handle text change in editable mode
    const handleTextChange = (e) => {
        if (onContentChange) {
            onContentChange(e.target.value);
        }
    };

    // Handle click to start editing
    const handleContentClick = () => {
        if (isEditable && !isStreaming) {
            setIsEditing(true);
        }
    };

    // Handle blur to stop editing
    const handleBlur = () => {
        setIsEditing(false);
    };

    // Auto-focus textarea when editing starts
    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isEditing]);

    // Handle file selection for image upload
    const handleFileSelect = async (file) => {
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setUploadError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setUploadError('Image size must be less than 5MB');
            return;
        }

        setUploadError(null);

        // If parent provides onMediaChange, use it (parent handles upload)
        if (onMediaChange) {
            await onMediaChange(file);
        } else {
            // Handle upload internally
            setIsInternalUploading(true);
            try {
                const result = await vaultService.uploadFile(file, {
                    episodeId: episode?.id,
                    tags: ['linkedin', 'social-media', 'content-creation'],
                    description: `LinkedIn post image for ${episode?.title || 'content'}`
                });

                const asset = result.asset || result;

                // Set the uploaded media with its URL
                onMediaChange({
                    url: asset.downloadUrl || asset.url || asset.fileUrl,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    id: asset.id || asset.assetId
                });
            } catch (error) {
                console.error('Failed to upload media:', error);
                setUploadError('Failed to upload image. Please try again.');
            } finally {
                setIsInternalUploading(false);
            }
        }
    };

    // Handle input change event
    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
        // Reset input value to allow re-selecting the same file
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle drag events for IMAGE zone - using counter pattern to prevent flashing
    const handleImageDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();

        imageDragCounterRef.current++;
        if (imageDragCounterRef.current === 1) {
            // Check if dragging an image asset
            if (e.dataTransfer.types.includes('application/json')) {
                setIsDraggingImage(true);
            } else if (e.dataTransfer.types.includes('Files')) {
                setIsDraggingImage(true);
            }
        }
    };

    const handleImageDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        imageDragCounterRef.current--;
        if (imageDragCounterRef.current === 0) {
            setIsDraggingImage(false);
        }
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleImageDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Reset counter
        imageDragCounterRef.current = 0;
        setIsDraggingImage(false);

        // Check for vault asset drag (from VaultDrawer)
        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);

                // Only handle IMAGE assets from vault
                if (artifact && artifact.type === 'vault-asset' && artifact.assetType === 'image') {
                    onMediaChange({
                        url: artifact.downloadUrl,
                        name: artifact.fileName || artifact.title,
                        type: artifact.mimeType || 'image/jpeg',
                        size: artifact.size,
                        id: artifact.id
                    });
                    return;
                } else if (artifact && artifact.type === 'vault-asset' && artifact.assetType !== 'image') {
                    // Not an image - show error
                    setUploadError('Only images can be dropped here. Text should be dropped on the text area.');
                    setTimeout(() => setUploadError(null), 3000);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse vault asset:', err);
            }
        }

        // Handle file drops from OS - validate it's an image
        const file = e.dataTransfer.files?.[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                setUploadError('Only image files can be dropped here. Text should be dropped on the text area.');
                setTimeout(() => setUploadError(null), 3000);
                return;
            }
            handleFileSelect(file);
        }
    };

    // Handle drag events for TEXT zone - using counter pattern to prevent flashing
    const handleTextDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();

        textDragCounterRef.current++;
        if (textDragCounterRef.current === 1) {
            // Check if dragging a text asset
            if (e.dataTransfer.types.includes('application/json')) {
                setIsDraggingText(true);
            } else if (e.dataTransfer.types.includes('text/plain')) {
                setIsDraggingText(true);
            }
        }
    };

    const handleTextDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        textDragCounterRef.current--;
        if (textDragCounterRef.current === 0) {
            setIsDraggingText(false);
        }
    };

    const handleTextDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleTextDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Reset counter
        textDragCounterRef.current = 0;
        setIsDraggingText(false);

        // Check for vault asset drag (from VaultDrawer)
        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);

                // Only handle TEXT assets from vault
                if (artifact && artifact.type === 'vault-asset' && artifact.assetType === 'text' && artifact.content) {
                    if (onContentChange) {
                        onContentChange(artifact.content);
                    }
                    return;
                } else if (artifact && artifact.type === 'vault-asset' && artifact.assetType !== 'text') {
                    // Not text - show error
                    setUploadError('Only text content can be dropped here. Images should be dropped on the image area.');
                    setTimeout(() => setUploadError(null), 3000);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse vault asset:', err);
            }
        }

        // Handle plain text drops
        if (e.dataTransfer.types.includes('text/plain')) {
            const text = e.dataTransfer.getData('text/plain');
            if (text && onContentChange) {
                onContentChange(text);
            }
        }
    };

    // Handle remove image
    const handleRemoveImage = () => {
        if (onMediaChange) {
            onMediaChange(null);
        }
        setUploadError(null);
    };

    // Trigger file input click
    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    // Determine if upload is in progress (either internal or from parent)
    const showUploading = isUploading || isInternalUploading;

    return (
        <div className="flex flex-col h-full bg-[#f3f2ef] font-sans items-center pt-4">
            {/* Action Buttons - Save to Vault & Start Over */}
            {isEditable && content && (
                <div className="w-full max-w-[550px] mb-2 px-2">
                    <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-3 py-2">
                        <div className="flex items-center gap-2">
                            {saveSuccess ? (
                                <>
                                    <CheckCircle size={14} className="text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">Saved to Vault!</span>
                                </>
                            ) : hasUnsavedChanges ? (
                                <>
                                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                    <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={14} className="text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">Ready to save</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onStartOver}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all"
                            >
                                <RotateCcw size={14} />
                                Start Over
                            </button>
                            <button
                                onClick={async () => {
                                    if (onSaveToVault) {
                                        const success = await onSaveToVault();
                                        if (success) {
                                            setSaveSuccess(true);
                                            setTimeout(() => setSaveSuccess(false), 2000);
                                        }
                                    }
                                }}
                                disabled={isSavingToVault || !content}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isSavingToVault
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'
                                    }`}
                            >
                                {isSavingToVault ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={14} />
                                        Save to Vault
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Feed Content - Scrollable */}
            <div className="w-full max-w-full overflow-y-auto scrollbar-hide px-0">
                {/* Post Card */}
                <div className="bg-white rounded-lg border border-gray-300 shadow-sm mx-auto pt-3 pb-1">
                    {/* Post Header */}
                    <div className="px-4 mb-2 flex items-start justify-between">
                        <div className="flex gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                                    <span className="text-white font-bold text-xs">PG</span>
                                </div>
                                <div className="absolute -bottom-1 -right-1 bg-gray-600 rounded-full p-0.5 border-2 border-white">
                                    <div className="w-2.5 h-2.5 bg-gray-600 rounded-full" />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center gap-1 group cursor-pointer">
                                    <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 group-hover:underline">PodGuestly</h3>
                                    <span className="text-xs text-gray-500">- 1st</span>
                                </div>
                                <p className="text-xs text-gray-500 leading-tight">Podcast Platform for Creators</p>
                                <div className="flex items-center gap-1 mt-0.5">
                                    <span className="text-xs text-gray-500">2h - </span>
                                    <svg className="w-3 h-3 text-gray-500" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M8 1a7 7 0 107 7 7 7 0 00-7-7zM8 14a6 6 0 116-6 6 6 0 01-6 6zM8 6a1 1 0 100-2 1 1 0 000 2zm0 8a1 1 0 100-2 1 1 0 000 2zm0-4a1 1 0 100-2 1 1 0 000 2z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button className="text-blue-600 font-semibold text-sm hover:bg-blue-50 px-2 py-1 rounded transition-colors">
                                + Follow
                            </button>
                            <MoreHorizontal size={18} className="text-gray-500 cursor-pointer" />
                        </div>
                    </div>

                    {/* Post Content - Click to Edit or Display Mode (TEXT DROP ZONE) */}
                    <div
                        className="px-4 mb-2"
                        onDragEnter={handleTextDragEnter}
                        onDragLeave={handleTextDragLeave}
                        onDragOver={handleTextDragOver}
                        onDrop={handleTextDrop}
                    >
                        {isEditing && isEditable && !isStreaming ? (
                            <textarea
                                ref={textareaRef}
                                value={displayContent}
                                onChange={handleTextChange}
                                onBlur={handleBlur}
                                className={`w-full text-sm text-gray-900 leading-relaxed whitespace-pre-wrap resize-none border transition-all outline-none ${isDraggingText ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-indigo-300 focus:ring-2 focus:ring-indigo-100'} rounded-lg p-2 -mx-2 -my-2`}
                                placeholder="Write your LinkedIn post here..."
                                rows={displayContent.split('\n').length + 2}
                            />
                        ) : (
                            <div
                                onClick={handleContentClick}
                                className={`text-sm text-gray-900 leading-relaxed whitespace-pre-wrap transition-all rounded-lg ${isDraggingText ? 'bg-indigo-50 border-2 border-dashed border-indigo-400 p-8 min-h-[120px] flex items-center justify-center' : 'p-2 -mx-2 -my-2'} ${isEditable && !isStreaming ? 'cursor-text hover:bg-gray-50' : ''}`}
                            >
                                {isDraggingText ? (
                                    <div className="text-center">
                                        <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-indigo-100 flex items-center justify-center">
                                            <FileText size={24} className="text-indigo-600" />
                                        </div>
                                        <span className="text-indigo-600 font-semibold text-base">
                                            Drop text content here
                                        </span>
                                        <p className="text-indigo-400 text-xs mt-1">
                                            Release to insert text
                                        </p>
                                    </div>
                                ) : (
                                    <p className="whitespace-pre-wrap leading-relaxed">
                                        {formatContent(displayContent)}
                                        {isStreaming && (
                                            <span className="inline-block w-1.5 h-4 bg-blue-500 animate-pulse ml-0.5" />
                                        )}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Media Content - Interactive Upload Zone (IMAGE DROP ZONE) */}
                    <div
                        className={`w-full bg-gray-100 aspect-video relative mb-2 group ${!media && !showUploading ? 'cursor-pointer' : ''}`}
                        onClick={!media && !showUploading ? handleUploadClick : undefined}
                        onDragEnter={handleImageDragEnter}
                        onDragLeave={handleImageDragLeave}
                        onDragOver={handleImageDragOver}
                        onDrop={handleImageDrop}
                    >
                        {/* Hidden file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleInputChange}
                            className="hidden"
                        />

                        {showUploading ? (
                            /* Loading State */
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                                <div className="text-center">
                                    <div className="mx-auto w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center mb-2">
                                        <Loader2 size={24} className="text-blue-600 animate-spin" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-700">Uploading...</p>
                                </div>
                            </div>
                        ) : media ? (
                            /* Uploaded Image Preview */
                            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center overflow-hidden">
                                <img
                                    src={media.url}
                                    alt="Uploaded media"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        console.error('Image failed to load:', media.url);
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                {/* Fallback if image fails to load */}
                                <div
                                    className="absolute inset-0 bg-gray-200 flex items-center justify-center"
                                    style={{ display: 'none' }}
                                >
                                    <div className="text-center">
                                        <AlertCircle size={24} className="text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-gray-500">Failed to load image</p>
                                    </div>
                                </div>
                                {/* Remove Image Button */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveImage();
                                    }}
                                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-colors opacity-0 group-hover:opacity-100"
                                    title="Remove image"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            /* Upload Placeholder */
                            <div className={`absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center transition-colors ${isDraggingImage ? 'bg-blue-100' : ''}`}>
                                <div className="text-center">
                                    <div className={`mx-auto w-12 h-12 rounded-lg shadow-sm flex items-center justify-center mb-2 transition-colors ${isDraggingImage ? 'bg-blue-100' : 'bg-white'}`}>
                                        <Image size={24} className={`${isDraggingImage ? 'text-blue-700' : 'text-blue-600'}`} />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-700">
                                        {isDraggingImage ? 'Drop image here' : (guestName ? `Episode with ${guestName}` : 'Episode Artwork')}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Click or drag to upload
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Upload Error Message */}
                    {uploadError && (
                        <div className="px-4 mb-2">
                            <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 px-3 py-2 rounded-lg">
                                <AlertCircle size={14} />
                                <span>{uploadError}</span>
                            </div>
                        </div>
                    )}

                    {/* Reactions Count */}
                    <div className="px-4 py-1 flex items-center justify-between text-xs text-gray-500 border-b border-gray-100 pb-2">
                        <div className="flex items-center gap-1 cursor-pointer hover:text-blue-600 hover:underline">
                            <div className="flex -space-x-1">
                                <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center z-30 ring-2 ring-white">
                                    <ThumbsUp size={8} className="text-white fill-white" />
                                </div>
                                <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center z-20 ring-2 ring-white">
                                    <Heart size={8} className="text-white fill-white" />
                                </div>
                                <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center z-10 ring-2 ring-white">
                                    <div className="text-[6px] text-white">-)</div>
                                </div>
                            </div>
                            <span className="ml-1 hover:text-blue-600 hover:underline">247</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="hover:text-blue-600 hover:underline cursor-pointer">12 comments</span>
                            <span>-</span>
                            <span className="hover:text-blue-600 hover:underline cursor-pointer">3 reposts</span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="px-2 py-1 flex items-center justify-between mt-1">
                        <button className="flex items-center gap-1.5 text-gray-500 hover:bg-gray-100 px-3 py-3 rounded-md transition-colors flex-1 justify-center group">
                            <ThumbsUp size={18} className="group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-semibold">Like</span>
                        </button>
                        <button className="flex items-center gap-1.5 text-gray-500 hover:bg-gray-100 px-3 py-3 rounded-md transition-colors flex-1 justify-center group">
                            <MessageCircle size={18} className="group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-semibold">Comment</span>
                        </button>
                        <button className="flex items-center gap-1.5 text-gray-500 hover:bg-gray-100 px-3 py-3 rounded-md transition-colors flex-1 justify-center group">
                            <Repeat2 size={18} className="group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-semibold">Repost</span>
                        </button>
                        <button className="flex items-center gap-1.5 text-gray-500 hover:bg-gray-100 px-3 py-3 rounded-md transition-colors flex-1 justify-center group">
                            <Send size={18} className="group-hover:scale-110 transition-transform -rotate-45 mb-1" />
                            <span className="text-sm font-semibold">Send</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LinkedInPreview;
